'use client';

import { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform, type MotionValue } from 'framer-motion';

type LabelRow = {
    key: string;
    src: string;
    reverse?: boolean;
};

const rows: LabelRow[] = [
    { key: 'label-one', src: '/assets/brand-labels/brand-label-one.svg' },
    { key: 'label-two', src: '/assets/brand-labels/brand-label-two.svg', reverse: true },
    { key: 'label-three', src: '/assets/brand-labels/brand-label-three.svg' },
    { key: 'label-four', src: '/assets/brand-labels/brand-label-four.svg', reverse: true },
];

export default function BrandLabelScrollBand({ locale }: { locale: 'en' | 'ar' }) {
    const sectionRef = useRef<HTMLElement>(null);
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start end', 'end start'],
    });
    const isAr = locale === 'ar';

    return (
        <section ref={sectionRef} className="relative overflow-hidden bg-brand-text py-20 text-[#081821] md:py-28">
            <div className="mx-auto mb-12 flex max-w-7xl flex-col gap-5 px-6 md:flex-row md:items-end md:justify-between">
                <div>
                    <p className="mb-4 text-sm font-bold uppercase tracking-[0.22em] text-[#081821]">
                        {isAr ? 'نظام نبراس' : 'nibras system'}
                    </p>
                    <h2 className="max-w-3xl font-heading text-5xl font-bold leading-none md:text-7xl">
                        {isAr ? 'هوية تتحرك باستمرار.' : 'Identity that keeps moving.'}
                    </h2>
                </div>
                <p className="max-w-md text-lg leading-relaxed text-[#081821]/80">
                    {isAr
                        ? 'يبقى إطار الشعار ثابتاً بينما تتحرك العلامة الداخلية مع الصفحة، مما يحول أصول العلامة التجارية إلى حركة بدلاً من مجرد زينة.'
                        : 'The label frame stays fixed while the internal mark shifts with the page, turning brand assets into motion instead of decoration.'}
                </p>
            </div>

            <div className="space-y-5 md:space-y-7">
                {rows.map((row) => (
                    <LabelRailRow key={row.key} row={row} progress={scrollYProgress} />
                ))}
            </div>
        </section>
    );
}

function LabelRailRow({ row, progress }: { row: LabelRow; progress: MotionValue<number> }) {
    const reduceMotion = useReducedMotion();
    const start = row.reverse ? '12vw' : '-12vw';
    const end = row.reverse ? '-12vw' : '12vw';
    /* Measured against the viewport rather than a CSS view() timeline: both the
       section and the row below it are `overflow-hidden`, which makes them scroll
       containers that a view() timeline would bind to instead of the page. */
    const x = useTransform(progress, [0, 1], [start, reduceMotion ? start : end]);

    return (
        <div className="relative -mx-[8vw] h-[clamp(9rem,12.5vw,13.5rem)] overflow-hidden">
            <div className="absolute left-1/2 top-1/2 w-[150vw] min-w-[92rem] -translate-x-1/2 -translate-y-1/2">
                <motion.div style={{ x }}>
                    {/* These used to be four enormous inline SVG trees. External,
                        lazy images preserve the artwork while removing hundreds of
                        KB from HTML/RSC and hundreds of DOM nodes from initial layout. */}
                    <img
                        src={row.src}
                        alt=""
                        aria-hidden="true"
                        width={1964}
                        height={282}
                        loading="lazy"
                        decoding="async"
                        className="block h-auto w-full drop-shadow-[0_18px_44px_rgba(0,0,0,0.12)]"
                    />
                </motion.div>
            </div>
        </div>
    );
}
